import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import { leadsApi, LeadStatus } from '../../services/leadsApi';

const STATUS_COLORS: Record<LeadStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  new: 'info',
  contacted: 'warning',
  qualified: 'default',
  converted: 'success',
  lost: 'error',
};

const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  converted: 'Converted',
  lost: 'Lost',
};

export const LeadDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [isEditing, setIsEditing] = useState(false);
  const [status, setStatus] = useState<LeadStatus>('new');
  const [notes, setNotes] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch lead
  const { data: lead, isLoading, error } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => leadsApi.getLead(id!),
    enabled: !!id,
  });

  // Sync state when lead data changes
  useEffect(() => {
    if (lead) {
      setStatus(lead.status);
      setNotes(lead.notes || '');
    }
  }, [lead]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (payload: { status?: LeadStatus; notes?: string }) =>
      leadsApi.updateLead(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: 'Lead updated successfully!',
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to update lead',
        severity: 'error',
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => leadsApi.deleteLead(id!),
    onSuccess: () => {
      navigate('/leads');
    },
  });

  // Convert mutation
  const convertMutation = useMutation({
    mutationFn: () => leadsApi.convertToClient(id!),
    onSuccess: (data) => {
      navigate(`/clients/${data.client._id}`);
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to convert lead to client',
        severity: 'error',
      });
    },
  });

  const handleSave = () => {
    updateMutation.mutate({ status, notes });
  };

  const handleConfirmDelete = () => {
    deleteMutation.mutate();
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !lead) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Failed to load lead details. Please try again later.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/leads')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700}>
              {lead.firstName} {lead.lastName}
            </Typography>
            <Chip
              label={STATUS_LABELS[lead.status]}
              color={STATUS_COLORS[lead.status]}
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {lead.status !== 'converted' && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => convertMutation.mutate()}
              disabled={convertMutation.isPending}
            >
              {convertMutation.isPending ? 'Converting...' : 'Convert to Client'}
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Contact Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Contact Information
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon color="action" fontSize="small" />
                <Typography>
                  <a href={`mailto:${lead.email}`} style={{ color: 'inherit' }}>
                    {lead.email}
                  </a>
                </Typography>
              </Box>

              {lead.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon color="action" fontSize="small" />
                  <Typography>
                    <a href={`tel:${lead.phone}`} style={{ color: 'inherit' }}>
                      {lead.phone}
                    </a>
                  </Typography>
                </Box>
              )}

              {lead.company && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon color="action" fontSize="small" />
                  <Typography>{lead.company}</Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Created
            </Typography>
            <Typography gutterBottom>
              {new Date(lead.createdAt).toLocaleString()}
            </Typography>

            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
              Source
            </Typography>
            <Typography>
              {lead.source === 'landing_page' ? 'Landing Page' : lead.source}
            </Typography>
          </Paper>
        </Grid>

        {/* Challenge & Strategy */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Challenge Submitted
            </Typography>
            <Typography
              sx={{
                bgcolor: 'grey.50',
                p: 2,
                borderRadius: 1,
                whiteSpace: 'pre-wrap',
              }}
            >
              {lead.challengeText || 'No challenge text provided.'}
            </Typography>
          </Paper>

          {lead.strategyPlan && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                AI-Generated Strategy Plan
              </Typography>
              <Typography
                sx={{
                  bgcolor: 'primary.50',
                  p: 2,
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {lead.strategyPlan}
              </Typography>
            </Paper>
          )}

          {/* Status & Notes */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Status & Notes
              </Typography>
              {!isEditing ? (
                <Button onClick={() => setIsEditing(true)}>Edit</Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              )}
            </Box>

            <FormControl fullWidth sx={{ mb: 3 }} disabled={!isEditing}>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => setStatus(e.target.value as LeadStatus)}
              >
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Notes"
              multiline
              rows={4}
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!isEditing}
              placeholder="Add notes about this lead..."
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Lead</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the lead for{' '}
            <strong>
              {lead.firstName} {lead.lastName}
            </strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LeadDetailPage;
